// Colorado College: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Colorado College's Common Data Set 2025-2026 (Institutional Planning
//    and Effectiveness): C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C21/C22 plans, G1/G5 2026-2027 costs. C1
//    gives no total line for enrollees; 510 is the sum of its rows (247 +
//    252 + 11). C21 labels its early decision counts "For the Fall 2024
//    entering class", so no early decision rate is shown. The C8 check
//    boxes do not survive in the text layer, so the test policy is read from
//    the admissions page.
//  - CC Admission: First-Year Students (deadlines and decision release,
//    test-optional policy) and Early Applicant Information (the binding
//    terms). The First-Year page says Regular Action decisions come in early
//    March; the Early Applicant page's FAQ says late March.
//  - College Scorecard API, school id 126678 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Colorado College Common Data Set 2025-2026',
        url: 'https://www.coloradocollege.edu/offices/ipe/documents/CDS-2025-2026_PDF_Final.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Colorado College Admission, First-Year Students',
        url: 'https://www.coloradocollege.edu/admission/apply/first-year-students.html',
        read: '2026-10-01',
    },
    early: {
        label: 'Colorado College Admission, Early Applicant Information',
        url: 'https://www.coloradocollege.edu/admission/apply/early.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?126678-Colorado-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'colorado-college',
    kbId: 'colorado_college',
    name: 'Colorado College',
    shortName: 'Colorado College',
    aliases: ['CC'],
    location: 'Colorado Springs, CO',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Colorado College colors); decoration only.
    branding: {
        primary: '#EAB337',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/colorado_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 8822,
        admitted: 1949,
        enrolled: 510,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state (Colorado)', applied: 1605, admitted: 300 },
            { group: 'Out-of-state', applied: 4847, admitted: 1485 },
            { group: 'International', applied: 2370, admitted: 164 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Colorado College admits to the college as a whole, and its Common Data Set reports one first-year acceptance rate with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'CC has Early Decision I and II and a non-binding Early Action round. The Common Data Set labels its early decision counts as fall 2024 figures and gives no early action counts, so it gives no early round rate for fall 2025.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Action', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-January', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Mid-February', binding: true },
            { plan: 'Regular Action', deadline: 'January 15', decision: 'March', binding: false },
        ],
        note: 'If you apply for financial aid, all aid documents, including the FAFSA and CSS Profile, are due by the admission deadline you choose. The First-Year page gives early March for Regular Action decisions; the Early Applicant page says late March.',
        source: 'firstYear',
        binding: 'Early Decision I and II are binding: if admitted for any entry term you select, you submit your enrollment deposit, withdraw all active applications and submit no others. If the financial aid offer does not make attending possible, you may decline and be released.',
        bindingSource: 'early',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Colorado College is test-optional: you choose on your application whether to send SAT or ACT scores. If you send them, CC considers them only if they strengthen your application.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75702 },
            { item: 'Required fees', amount: 528 },
            { item: 'Housing (on campus)', amount: 9616 },
            { item: 'Food (on-campus meal plan)', amount: 7744 },
            { item: 'Books and supplies', amount: 1240 },
            { item: 'Transportation', amount: 1230 },
            { item: 'Other expenses', amount: 1800 },
        ],
        total: 97860,
        note: 'For a full-time student living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 27951,
            byIncome: [
                { income: '$0 to $30,000', amount: 13592 },
                { income: '$30,001 to $48,000', amount: 13250 },
                { income: '$48,001 to $75,000', amount: 13798 },
                { income: '$75,001 to $110,000', amount: 28292 },
                { income: 'Over $110,000', amount: 43403 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Colorado College\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
